import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import PinInput from '@/components/common/PinInput/PinInput'
import ResendCode from '@components/auth/ResendCode/ResendCode'

import MotionText from '@/components/common/Motion/MotionText'
import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import HeaderBack from '@/components/dashboard/Header/HeaderBack'

import { useKycStore } from '@store/useKycStore'
import { useUserStore } from '@/store/useUserStore'
import { usePageSetup } from '@/hooks/usePageSetup'

import mfaAPI from '@/services/api/mfa'

export const KycMfaCodePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('kyc.pageTitle.mfaCode'), { scroll: true })

  const user = useUserStore((state) => state.user)

  const setUser = useUserStore((state) => state.setUser)

  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(8), [])

  const [error, setError] = useState<string | undefined>()

  const navigate = useNavigate()
  const location = useLocation()
  const phone = location.state?.phone

  const pinSubmit = async (code: string) => {

    try {
      await mfaAPI.verify({ mfa: { phone, code } })

      if (user) {
        await setUser({ ...user, mfa: true })
      }

      /* TODO: Check for attribution if from bolt */
      /* Because this handbook is cooked for Bolt documents */
      navigate("/handbook/journal")
    } catch (error: any) {
      setError(t('kyc.mfaCode.invalidCode'))
    }
  }

  return (
    <form className="h-full bg-white p-5 pb-8 flex flex-col">
      <HeaderBack className='items-center gap-4'>
        <ProgressBar
          currentStep={progress}
          totalSteps={8}
        />
      </HeaderBack>
      <div className="flex-1 flex flex-col mt-4">
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('kyc.mfaCode.title')}
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-base"
            delay={0.4}
          >
            {t('kyc.mfaCode.description', { phone })}
          </MotionText>
        </div>
        <motion.div className='my-6 flex flex-row gap-2'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut"
          }}
        >
          <PinInput
            onChange={(value) => value.length === 6 && pinSubmit(value)}
            error={error}
          />
        </motion.div>
        <ResendCode
          initialSeconds={59}
          onResend={async () => mfaAPI.setup({ mfa: { phone } })}
        />
      </div>
    </form>
  )
}

export default KycMfaCodePage